import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import {
  IdentityConciliationStatus,
  IdentityVerificationStatus,
  IdentityVerificationStatusDto,
  StripeVerificationSessionDto,
} from '@workspace/data-types';
import { StripeIdentityService, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, expand, switchMap, take, tap, timer } from 'rxjs';

@Component({
  selector: 'core-user-identity-verification',
  standalone: true,
  imports: [DatePipe, MatCardModule, MatButtonModule, MatIconModule],
  templateUrl: './user-identity-verification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserIdentityVerificationCoreComponent implements OnInit {
  private readonly apiService = inject(ApiService);
  private readonly stripeIdentityService = inject(StripeIdentityService);
  private readonly userStore = inject(UserStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly Status = IdentityVerificationStatus;
  readonly ConciliationStatus = IdentityConciliationStatus;
  readonly delays = [5000, 10000, 20000, 40000, 80000, 160000]; // Incremental delays
  attempt = 0;

  isRefreshing = signal(false);
  refreshCooldown = signal(0);

  status = computed(
    () => this.userStore.currentUser()?.identity?.verificationStatus ?? IdentityVerificationStatus.UNVERIFIED,
  );
  identityStatus = signal<IdentityVerificationStatusDto | null>(null);
  currentSession = signal<StripeVerificationSessionDto | null>(null);

  readonly lastSession = computed(() => {
    return (
      this.currentSession() ??
      this.userStore.currentUser()?.metadata?.stripe?.verification?.sessions?.[0] ??
      null
    );
  });

  canRestartVerification = computed(() => {
    const session = this.currentSession();
    if (!session || session.status !== IdentityVerificationStatus.PROCESSING) return false;

    const createdAt = new Date(session.createdAt);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

    return createdAt < fiveMinutesAgo;
  });

  // Customization inputs
  title = input<string>("Vérification d'identité");
  description = input<string>(
    "Pour garantir la sécurité de notre plateforme, nous vérifions l'identité de chaque utilisateur. C'est simple, rapide et entièrement sécurisé.",
  );
  successMessage = input<string>('Félicitations, votre identité a été validée avec succès.');
  successActionLabel = input<string>('Continuer');

  // Events
  continued = output<void>();

  ngOnInit() {
    this.refreshUser();
  }

  onVerifyIdentity() {
    this.stripeIdentityService
      .verifyIdentity()
      .pipe(
        tap(() => this.refreshUser()),
        catchError((err) => {
          console.error('Identity verification failed', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  refreshUser() {
    if (this.isRefreshing() || this.refreshCooldown() > 0) return;

    this.isRefreshing.set(true);
    this.startCooldown(10);

    this.apiService
      .user()
      .identityVerification()
      .status()
      .pipe(
        tap((identityVerificationStatus) => {
          this.isRefreshing.set(false);
          this.identityStatus.set(identityVerificationStatus);
          this.currentSession.set(identityVerificationStatus.session);

          const newStatus =
            identityVerificationStatus.userIdentity?.verificationStatus ??
            this.userStore.currentUser()?.identity?.verificationStatus ??
            IdentityVerificationStatus.UNVERIFIED;

          if (this.status() !== newStatus) {
            this.userStore.updateCurrentUserIdentityVerificationStatus(newStatus);
          }
        }),
        expand(({ session }) => {
          if (
            session?.status === IdentityVerificationStatus.PROCESSING &&
            this.attempt < this.delays.length
          ) {
            return timer(this.delays[this.attempt++]).pipe(
              tap(() => this.isRefreshing.set(true)),
              switchMap(() => this.apiService.user().identityVerification().status()),
              tap((identityVerificationStatus) => {
                this.isRefreshing.set(false);
                this.identityStatus.set(identityVerificationStatus);
                this.currentSession.set(identityVerificationStatus.session);

                const newStatus =
                  identityVerificationStatus.userIdentity?.verificationStatus ??
                  this.userStore.currentUser()?.identity?.verificationStatus ??
                  IdentityVerificationStatus.UNVERIFIED;

                if (this.status() !== newStatus) {
                  this.userStore.updateCurrentUserIdentityVerificationStatus(newStatus);
                }
              }),
            );
          }
          return EMPTY;
        }),
        catchError((err) => {
          this.isRefreshing.set(false);
          console.error('Failed to refresh identity verification status', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private startCooldown(seconds: number) {
    this.refreshCooldown.set(seconds);
    timer(0, 1000)
      .pipe(
        take(seconds + 1),
        tap((v) => this.refreshCooldown.set(seconds - v)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
